import { useEffect } from 'react'

interface SEOProps {
  title?: string
  description?: string
  image?: string
  url?: string
  type?: 'website' | 'article' | 'music.song'
}

const BASE_URL = 'https://thearchivenetwork.com'
const DEFAULT_TITLE = 'The Archive Network'
const DEFAULT_DESCRIPTION = 'The unified gateway to humanity\'s memory. A growing network of interconnected archives documenting treaties, conflicts, civilizations, company origins, lineages, classified secrets, explorations, and mythology.'
const DEFAULT_IMAGE = `${BASE_URL}/images/og-image.jpg`

export function useSEO({ title, description, image, url, type = 'website' }: SEOProps = {}) {
  useEffect(() => {
    // Update document title
    const fullTitle = title ? `${title} | ${DEFAULT_TITLE}` : DEFAULT_TITLE
    document.title = fullTitle

    // Helper to update meta tags
    const updateMeta = (property: string, content: string) => {
      let meta = document.querySelector(`meta[property="${property}"]`) as HTMLMetaElement
      if (!meta) {
        meta = document.querySelector(`meta[name="${property}"]`) as HTMLMetaElement
      }
      if (meta) {
        meta.content = content
      }
    }

    // Update Open Graph tags
    updateMeta('og:title', fullTitle)
    updateMeta('og:description', description || DEFAULT_DESCRIPTION)
    updateMeta('og:image', image || DEFAULT_IMAGE)
    updateMeta('og:url', url || BASE_URL)
    updateMeta('og:type', type)

    // Update Twitter tags
    updateMeta('twitter:title', fullTitle)
    updateMeta('twitter:description', description || DEFAULT_DESCRIPTION)
    updateMeta('twitter:image', image || DEFAULT_IMAGE)
    updateMeta('twitter:url', url || BASE_URL)

    // Update description meta
    updateMeta('description', description || DEFAULT_DESCRIPTION)

    // Update canonical. The static canonical was removed from index.html (it
    // wrongly declared every SPA route a duplicate of the homepage for non-JS
    // crawlers). Create the tag if missing so JS crawlers still get a correct
    // per-page canonical; default is self-referential, never the bare homepage.
    let canonical = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null
    if (!canonical) {
      canonical = document.createElement('link')
      canonical.rel = 'canonical'
      document.head.appendChild(canonical)
    }
    canonical.href = url || `${BASE_URL}${window.location.pathname}`

    // Cleanup: Reset to defaults when component unmounts
    return () => {
      document.title = DEFAULT_TITLE
      updateMeta('og:title', DEFAULT_TITLE)
      updateMeta('og:description', DEFAULT_DESCRIPTION)
      updateMeta('og:image', DEFAULT_IMAGE)
      updateMeta('og:url', BASE_URL)
      updateMeta('og:type', 'website')
      updateMeta('twitter:title', DEFAULT_TITLE)
      updateMeta('twitter:description', DEFAULT_DESCRIPTION)
      updateMeta('twitter:image', DEFAULT_IMAGE)
      updateMeta('twitter:url', BASE_URL)
      updateMeta('description', DEFAULT_DESCRIPTION)
      // Canonical intentionally NOT reset to BASE_URL here — doing so would
      // reintroduce the homepage-canonical-on-every-page bug during SPA nav.
      // The next route's useSEO sets its own canonical.
    }
  }, [title, description, image, url, type])
}
